import invariant from 'invariant'

import {resolveDimension} from './strategies'
import type {
  Offset,
  Size,
  Rectangle,
  StrategyResult,
  StrategyOptions,
  RatingOptions,
} from './types'

export function rateVisibility(
  {left, top}: Offset,
  {width, height}: Size,
  {
    top: containerTop,
    left: containerLeft,
    width: containerWidth,
    height: containerHeight,
  }: Rectangle
): number {
  // elements with 0 width/height should be visible if their position is visible
  const visibleHeight =
    Math.min(top + (height || 1), containerTop + containerHeight) -
    Math.max(top, containerTop)
  const visibleWidth =
    Math.min(left + (width || 1), containerLeft + containerWidth) -
    Math.max(left, containerLeft)
  return (
    Math.min(1, Math.max(0, visibleHeight / height)) *
      Math.min(1, Math.max(0, visibleWidth / width)) || 0
  )
}

export function rateShift(
  result: {
    shiftedX?: number
    shiftedY?: number
  },
  {
    elementRect,
  }: {
    readonly elementRect: Size
  }
): number {
  let rating = 1
  if (result.shiftedX !== undefined) {
    rating *= 1 - Math.min(1, Math.abs(result.shiftedX / elementRect.width))
  }
  if (result.shiftedY !== undefined) {
    rating *= 1 - Math.min(1, Math.abs(result.shiftedY / elementRect.height))
  }
  return rating || 0
}

export function rateAnchorVisibility(
  result: {
    left: number
    top: number
    anchorX?: number
    anchorY?: number
  },
  {
    targetVisibleRect,
    visibleRect,
  }: {
    readonly targetVisibleRect?: Rectangle
    readonly visibleRect?: Rectangle
  }
): number {
  let rating = 1
  const rect = targetVisibleRect || visibleRect
  if (!rect) return rating
  const anchorX = (result.anchorX || 0) + result.left
  const anchorY = (result.anchorY || 0) + result.top
  rating *= anchorX >= rect.left && anchorX < rect.left + rect.width ? 1 : 0
  rating *= anchorY >= rect.top && anchorY < rect.top + rect.height ? 1 : 0
  return rating
}

// rate space available to the element,
// by checking if reserved space fits entirely in available space
export function rateReserve(
  result: {
    maxWidth?: number
    maxHeight?: number
  },
  options: StrategyOptions & RatingOptions
): number {
  const {maxWidth, maxHeight} = result
  const {reserveWidth = 0, reserveHeight = 0, targetRect} = options
  if (maxWidth === undefined || maxHeight === undefined) return 1
  if (!reserveWidth && !reserveHeight) return 1

  return rateVisibility(
    {left: 0, top: 0},
    {
      width: resolveDimension(reserveWidth, targetRect.width, options),
      height: resolveDimension(reserveHeight, targetRect.height, options),
    },
    {left: 0, top: 0, width: maxWidth, height: maxHeight}
  )
}

export default function rateStrategy(
  result: StrategyResult,
  options: StrategyOptions & RatingOptions
): number {
  let rating = 1
  const {
    elementRect,
    containerRect,
    visibleRect,
    visibleWeight = 1,
    shiftWeight = 0,
    reserveWeight = visibleWeight,
  } = options

  if (visibleWeight && containerRect) {
    rating *=
      (rateVisibility(result, elementRect, containerRect) ||
        // if element "hides" completely in the padding, use visibleRect with lower weight
        (visibleRect
          ? rateVisibility(result, elementRect, visibleRect) * 0.5
          : 0)) *
        visibleWeight +
      1 -
      visibleWeight

    if (reserveWeight) {
      rating *= rateReserve(result, options) * reserveWeight + 1 - reserveWeight
    }
  }
  if (
    shiftWeight &&
    (result.shiftedX !== undefined || result.shiftedY !== undefined)
  ) {
    rating *= rateShift(result, options) * shiftWeight + 1 - shiftWeight
  }
  if (DEVELOPMENT) invariant(!isNaN(rating), 'Resulting rating is NaN', result)
  return rating
}

export function rateTarget(
  result: StrategyResult,
  options: StrategyOptions & RatingOptions
): number {
  let rating = 1
  const {
    targetRect,
    visibleRect,
    targetVisibleRect,
    targetVisibleWeight = 1,
    anchorVisibleWeight = 0,
  } = options
  const rect = targetVisibleRect || visibleRect
  if (targetVisibleWeight && rect) {
    rating *=
      rateVisibility(targetRect, targetRect, rect) * targetVisibleWeight +
      1 -
      targetVisibleWeight
  }
  if (anchorVisibleWeight && rect) {
    rating *=
      rateAnchorVisibility(result, options) * anchorVisibleWeight +
      1 -
      anchorVisibleWeight
  }
  if (DEVELOPMENT) invariant(!isNaN(rating), 'Resulting rating is NaN', result)
  return rating
}
